import BaseComponent from './BaseComponent';
import { gsap } from 'gsap';
import { getKeyboardFocusableElements, trapFocus } from '../utils';

export default class MenuLayers extends BaseComponent {
	constructor(header, menuInstance) {
		super();

		// Elements
		this.header = header;
		this.headerLogo = this.header.elm.querySelector('[data-header-logo]');
		this.headerPreviousButton = this.header.elm.querySelector('[data-header-previous-button]');
		this.headerCurrentLabel = this.header.elm.querySelector('[data-menu-current-label]');
		this.vacancyButton = this.header.elm.querySelector('[data-vacancy-cta]');

		this.menuInstance = menuInstance;
		this.menu = this.menuInstance.elm;
		this.menuItems = this.menu.querySelectorAll('[data-menu-item]');
		this.menuButtons = this.menu.querySelectorAll('[data-menu-parent-toggle]');
		this.subMenus = this.menu.querySelectorAll('[data-menu-submenu]');
		this.subMenuHeaderLinks = this.menu.querySelectorAll('[data-menu-submenu-header-link]');

		this.breakpoint = window.matchMedia('(min-width: 992px)');
		this.reduceMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

		// State
		this.activeMenuItem = null;
		this.activeSubMenuItem = null;

		// Listeners
		this.breakpoint.addEventListener('change', () => {
			this.initialState();
			this.setListeners();
		});

		this.headerPreviousButton.addEventListener('click', (e) => {
			e.preventDefault();
			if (this.activeMenuItem) {
				this.closeSubMenu(this.activeMenuItem);
			}
		});

		this.setListeners();
		this.initialState();

		document.addEventListener('leave', () => {
			this.initialState();
		});
	}

	setListeners() {
		this.menuButtons.forEach((menuButton) => {
			if (!this.breakpoint.matches) {
				menuButton.addEventListener('click', (e) => {
					e.preventDefault();
					const menuItem = menuButton.closest('[data-menu-item]');
					if (!this.breakpoint.matches) {
						this.openSubMenu(menuItem);
					}
				});
			}
		});
	}

	initialState() {
		if (this.breakpoint.matches) {
			return;
		}

		this.headerCurrentLabel.textContent = '';

		this.menuItems.forEach((item) => {
			gsap.set(item, {
				autoAlpha: 1,
				pointerEvents: 'auto',
			});
		});

		gsap.set([this.menuButtons, this.headerLogo], {
			autoAlpha: 1,
			pointerEvents: 'auto',
		});

		gsap.set([this.headerPreviousButton, this.headerCurrentLabel, this.subMenus], {
			autoAlpha: 0,
			pointerEvents: 'none',
		});
	}

	openSubMenu(menuItem) {
		this.activeMenuItem = menuItem;

		const focusableElements = [
			this.headerPreviousButton,
			this.header.menuButton,
			...getKeyboardFocusableElements(this.activeMenuItem),
			this.vacancyButton,
		];
		if (this.trapFocusInstance) {
			this.trapFocusInstance.remove();
		}
		this.trapFocusInstance = trapFocus(this.header.elm, focusableElements);

		const subMenu = menuItem.querySelector('[data-menu-submenu]');
		const subMenuLinks = menuItem.querySelectorAll('[data-menu-submenu-links]');
		const menuButton = menuItem.querySelector('[data-menu-parent-toggle]');

		this.headerCurrentLabel.textContent = menuItem.querySelector('[data-menu-parent-label]').textContent;

		this.tl = gsap.timeline();

		this.tl.to([menuButton, this.headerLogo], {
			autoAlpha: 0,
			pointerEvents: 'none',
			duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
			ease: 'sine.out',
		});

		this.menuItems.forEach((item) => {
			if (item !== menuItem) {
				gsap.to(item, {
					autoAlpha: 0,
					pointerEvents: 'none',
					duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
					ease: 'sine.in',
				});
			}
		});

		this.tl.to([subMenu, this.headerPreviousButton, this.headerCurrentLabel, this.subMenuHeaderLinks], {
			autoAlpha: 1,
			pointerEvents: 'auto',
			duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
			ease: 'sine.out',
		});

		subMenuLinks.forEach((link) => {
			gsap.to(link, {
				autoAlpha: 1,
				pointerEvents: 'auto',
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
				ease: 'sine.out',
			});
		}, '<');
	}

	closeSubMenu(menuItem) {
		if (!menuItem) return;

		this.activeMenuItem = null;

		if (this.trapFocusInstance) {
			this.trapFocusInstance.remove();
			this.trapFocusInstance = null;
		}

		const subMenu = menuItem.querySelector('[data-menu-submenu]');
		const menuButton = menuItem.querySelector('[data-menu-parent-toggle]');

		this.tl = gsap.timeline();

		this.tl.to([subMenu, this.headerPreviousButton, this.headerCurrentLabel], {
			autoAlpha: 0,
			pointerEvents: 'none',
			duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
			ease: 'sine.in',
		});

		this.tl.to(
			[menuButton, this.headerLogo, this.menuItems],
			{
				autoAlpha: 1,
				pointerEvents: 'auto',
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
				ease: 'sine.out',
				onComplete: () => {
					this.headerCurrentLabel.textContent = '';
				},
			},
			'>'
		);
	}
}
